import { ImagePixels, QrDecoder, RawSymbol, ZxingDecoder } from '../../lib/brdocs';

/**
 * A {@link QrDecoder} that runs the zxing-wasm decode inside a Web Worker so the
 * heavy full-resolution scan never blocks the UI thread. Drop-in for the
 * main-thread {@link ZxingDecoder}: same `decode(image) => RawSymbol[]` contract,
 * so the registry's parsing + signature verification (which stay on the main
 * thread via `scanContext`) are unchanged.
 *
 * Robustness:
 *  - If Web Workers are unavailable, or the worker fails to construct, it falls
 *    back to a main-thread {@link ZxingDecoder} (correct, just not off-thread).
 *  - A decode that exceeds {@link timeoutMs} resolves to `[]` (treated as "no QR"
 *    → the caller's OCR-only path), so a pathological image can't hang the flow.
 *
 * The pixel buffer is **transferred** to the worker (not copied), which also frees
 * the ~tens-of-MB RGBA buffer from the main thread. Callers must therefore not
 * reuse the passed `ImagePixels` after `decode()`.
 */
export class WorkerQrDecoder implements QrDecoder {
  /**
   * Pixel ceiling for a main-thread fallback decode (~1.4 MP ≈ 1400×1000). Above
   * this, a synchronous zxing scan would block the UI long enough to look frozen,
   * so we skip QR instead. The worker path has no such limit.
   */
  private static readonly MAX_MAIN_THREAD_PX = 1_400_000;
  private worker: Worker | null = null;
  private workerBroken = false;
  private seq = 0;
  private readonly pending = new Map<
    number,
    { resolve: (s: RawSymbol[]) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private fallbackDecoder?: ZxingDecoder;

  constructor(
    private readonly workerFactory: () => Worker = defaultWorkerFactory,
    private readonly timeoutMs = 20_000,
    private readonly fallbackFactory: () => QrDecoder = () =>
      (this.fallbackDecoder ??= new ZxingDecoder({ wasmLocation: 'assets/zxing_reader.wasm' })),
  ) {}

  async decode(image: ImagePixels): Promise<RawSymbol[]> {
    const worker = this.ensureWorker();
    if (!worker) {
      // No worker (unsupported / blocked / crashed). Falling back to a main-thread
      // decode is only safe for a SMALL image — on a multi-megapixel one it would
      // re-create the very freeze the worker exists to prevent. So cap it: decode
      // small images inline; fail open (no QR → the caller's OCR-only path) on large
      // ones rather than block the tab.
      const px = image.width * image.height;
      if (px <= WorkerQrDecoder.MAX_MAIN_THREAD_PX) {
        return this.fallbackFactory().decode(image);
      }
      console.warn(
        `[WorkerQrDecoder] worker unavailable and image too large for a safe main-thread decode ` +
          `(${image.width}x${image.height}); skipping QR to avoid freezing the tab`,
      );
      return [];
    }

    const id = ++this.seq;
    // Transfer the ORIGINAL pixel buffer (no copy) — this both avoids a structured-
    // clone and frees the ~tens-of-MB RGBA buffer from the main thread. It detaches
    // `image.data`, so callers must not reuse `image` after this (they don't: each
    // heavy scan builds fresh pixels via getImageData). getImageData always yields a
    // buffer this view fully owns, so transferring the whole buffer is correct.
    const buffer = image.data.buffer;

    return new Promise<RawSymbol[]>((resolve) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) {
          console.warn('[WorkerQrDecoder] decode timed out — treating as no QR found');
          resolve([]);
        }
      }, this.timeoutMs);
      this.pending.set(id, { resolve, timer });
      worker.postMessage({ id, buffer, width: image.width, height: image.height }, [buffer]);
    });
  }

  /** Release the worker (idempotent). Call on teardown; a new decode re-creates it. */
  terminate(): void {
    for (const { resolve, timer } of this.pending.values()) {
      clearTimeout(timer);
      resolve([]);
    }
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (this.workerBroken || typeof Worker === 'undefined') return null;
    try {
      const worker = this.workerFactory();
      worker.onmessage = (ev: MessageEvent) => this.onMessage(ev);
      worker.onerror = (ev: ErrorEvent) => this.onWorkerError(ev);
      this.worker = worker;
      return worker;
    } catch (e) {
      console.warn('[WorkerQrDecoder] worker unavailable, falling back to main thread:', e);
      this.workerBroken = true;
      return null;
    }
  }

  private onMessage(ev: MessageEvent): void {
    const data = ev.data as
      | { id: number; ok: true; symbols: RawSymbol[] }
      | { id: number; ok: false; error: string };
    const entry = this.pending.get(data.id);
    if (!entry) return; // already timed out
    this.pending.delete(data.id);
    clearTimeout(entry.timer);
    if (data.ok) {
      entry.resolve(data.symbols ?? []);
    } else {
      // A decode error is fail-open: no symbols → the caller's OCR-only path.
      console.warn('[WorkerQrDecoder] decode error in worker:', data.error);
      entry.resolve([]);
    }
  }

  private onWorkerError(ev: ErrorEvent): void {
    // A hard worker crash: fail every in-flight decode open, drop the worker, and
    // mark it broken so subsequent decodes use the main-thread fallback.
    console.warn('[WorkerQrDecoder] worker crashed, falling back to main thread:', ev.message);
    for (const { resolve, timer } of this.pending.values()) {
      clearTimeout(timer);
      resolve([]);
    }
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
    this.workerBroken = true;
  }
}

/**
 * Default worker factory. The `new Worker(new URL(...))` literal must stay here so
 * the Angular/webpack builder statically detects and bundles the worker chunk.
 */
function defaultWorkerFactory(): Worker {
  return new Worker(new URL('./qr-decode.worker', import.meta.url));
}
